/**
 * Desktop push notification preferences component.
 *
 * Shows enable/disable controls and per-category toggles
 * using shadcn Card + Switch components.
 */

import { useCallback, useEffect, useState } from 'react';

import { Bell, BellOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { usePush } from '@/hooks/usePush';
import { useToast } from '@/hooks/useToast';
import { NotificationCategory, type NotificationPreference, pushService } from '@/services/push';

const CATEGORY_LABELS: Record<string, string> = {
  [NotificationCategory.TradeExecution]: 'Trade Execution',
  [NotificationCategory.AgentCompletion]: 'Agent Completion',
  [NotificationCategory.SmartNewsBreaking]: 'Breaking News',
  [NotificationCategory.SmartNewsCategoryAlert]: 'Category Alerts',
  [NotificationCategory.SurfConditions]: 'Surf Conditions',
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  [NotificationCategory.TradeExecution]: 'Get notified when trades are executed',
  [NotificationCategory.AgentCompletion]: 'Get notified when the agent completes a run',
  [NotificationCategory.SmartNewsBreaking]: 'Get notified about breaking news stories',
  [NotificationCategory.SmartNewsCategoryAlert]: 'Get notified about news in your followed categories',
  [NotificationCategory.SurfConditions]: 'Get notified when surf conditions improve at favorited beaches',
};

interface CategorySection {
  label: string;
  categories: NotificationCategory[];
}

const SECTIONS: CategorySection[] = [
  {
    label: 'Trading',
    categories: [NotificationCategory.TradeExecution, NotificationCategory.AgentCompletion],
  },
  {
    label: 'News',
    categories: [NotificationCategory.SmartNewsBreaking, NotificationCategory.SmartNewsCategoryAlert],
  },
  {
    label: 'Surf',
    categories: [NotificationCategory.SurfConditions],
  },
];

export function NotificationSettings() {
  const { isSubscribed, isLoading, permission, error, subscribe, unsubscribe } = usePush();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);

  useEffect(() => {
    if (!isSubscribed) return;
    pushService.getPreferences().then(setPreferences).catch(() => {});
  }, [isSubscribed]);

  const handleToggle = useCallback(
    async (category: string) => {
      const updated = preferences.map((p) =>
        p.category === category ? { ...p, enabled: !p.enabled } : p,
      );
      setPreferences(updated);
      try {
        await pushService.updatePreferences(updated);
        toast({ title: 'Saved', description: 'Notification preference saved', variant: 'success' });
      } catch {
        toast({ title: 'Error', description: 'Failed to save preference', variant: 'destructive' });
      }
    },
    [preferences, toast],
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (permission === 'denied') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>Push notifications for trade alerts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3">
            <BellOff className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              Notifications are blocked. Please enable them in your browser settings.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isSubscribed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>
            Enable push notifications to receive trade alerts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={subscribe}>
            <Bell className="h-4 w-4 mr-2" />
            Enable Notifications
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>Manage your notification preferences</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {SECTIONS.map((section) => (
          <div key={section.label} className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground">{section.label}</h3>
            <div className="space-y-4">
              {section.categories.map((category) => {
                const pref = preferences.find((p) => p.category === (category as string));
                const enabled = pref?.enabled ?? true;

                return (
                  <div key={category} className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">{CATEGORY_LABELS[category] ?? category}</p>
                      <p className="text-xs text-muted-foreground">
                        {CATEGORY_DESCRIPTIONS[category] ?? ''}
                      </p>
                    </div>
                    <Switch checked={enabled} onCheckedChange={() => handleToggle(category)} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="pt-2">
          <Button variant="outline" className="text-destructive" onClick={unsubscribe}>
            <BellOff className="h-4 w-4 mr-2" />
            Disable Notifications
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
